import { useState, useRef, useEffect, useCallback } from 'react';
import { useAppStore } from '@/store';
import { demoDatasets } from '@/data/demoDatasets';
import type { DemoDataset } from '@/data/demoDatasets';
import { Database, X } from 'lucide-react';
import styles from './DemoLoader.module.css';

export function DemoLoader() {
  const showLoadDemo = useAppStore((s) => s.showLoadDemo);
  const loadDemoDataset = useAppStore((s) => s.loadDemoDataset);
  const setActiveTab = useAppStore((s) => s.setActiveTab);
  const [isOpen, setIsOpen] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);

  const handleSelect = useCallback((dataset: DemoDataset) => {
    loadDemoDataset(dataset.rawInput, dataset.mode);
    if (dataset.crossContactRisks) {
      for (const risk of dataset.crossContactRisks) {
        useAppStore.getState().addCrossContactRisk(risk);
      }
    }
    setIsOpen(false);
    setActiveTab('results');
  }, [loadDemoDataset, setActiveTab]);

  const handleClose = useCallback(() => setIsOpen(false), []);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    modalRef.current?.focus();
  }, [isOpen]);

  if (!showLoadDemo) return null;

  return (
    <>
      <button
        className={styles.trigger}
        onClick={() => setIsOpen(true)}
        aria-label="Load demo dataset"
        title="Load demo"
      >
        <Database size={16} />
        <span className={styles.triggerLabel}>Load Demo</span>
      </button>

      {isOpen && (
        <div className={styles.overlay} onClick={handleClose} role="presentation">
          <div
            ref={modalRef}
            className={styles.modal}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Select a demo dataset"
            tabIndex={-1}
          >
            <div className={styles.modalHeader}>
              <h2 className={styles.modalTitle}>Load Demo Dataset</h2>
              <button className={styles.closeButton} onClick={handleClose} aria-label="Close">
                <X size={18} />
              </button>
            </div>
            <ul className={styles.datasetList}>
              {demoDatasets.map((dataset) => (
                <li key={dataset.name}>
                  <button
                    className={styles.datasetButton}
                    onClick={() => handleSelect(dataset)}
                  >
                    <span className={styles.datasetName}>{dataset.name}</span>
                    <span className={styles.datasetMode}>{dataset.mode}</span>
                    <span className={styles.datasetDesc}>{dataset.description}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </>
  );
}
